import { Controller } from "@hotwired/stimulus"

/**
 * Controller for the conversion action settings form in the drawer.
 *
 * Handles:
 * - Select all / deselect all checkboxes
 * - Indeterminate state for select all
 * - Dispatching settings-saved event to panel controller
 * - Closing the drawer after successful save
 */
export default class extends Controller {
  static targets = ["selectAll", "selectAllLabel", "checkbox"]
  static values = { provider: String }

  get visibleCheckboxes() {
    return this.checkboxTargets.filter(cb => {
      const row = cb.closest("[data-list-filter-target='item']")
      return !row || row.style.display !== "none"
    })
  }

  toggleAll() {
    const isChecked = this.selectAllTarget.checked
    this.visibleCheckboxes.forEach(checkbox => {
      checkbox.checked = isChecked
    })
  }

  toggleDestinations(event) {
    const caId = event.currentTarget.dataset.caId
    if (!caId) return

    const panel = this.element.querySelector(`[data-destinations-panel="${caId}"]`)
    const chevron = this.element.querySelector(`[data-destinations-chevron="${caId}"]`)
    if (panel) panel.classList.toggle("hidden")
    if (chevron) chevron.classList.toggle("rotate-180")
  }

  updateSelectAll() {
    if (!this.hasSelectAllTarget) return

    const visible = this.visibleCheckboxes
    const allChecked = visible.length > 0 && visible.every(cb => cb.checked)
    const someChecked = visible.some(cb => cb.checked)

    this.selectAllTarget.checked = allChecked
    this.selectAllTarget.indeterminate = someChecked && !allChecked

    if (this.hasSelectAllLabelTarget) {
      this.selectAllLabelTarget.textContent = `Select All (${visible.length})`
    }
  }

  /**
   * Called when form submission completes.
   * Dispatches event at document level for panel controller to catch,
   * then closes the drawer.
   */
  submitEnd(event) {
    // Check for successful submission - Turbo sets success for 2xx/3xx responses
    // Also check fetchResponse.succeeded for compatibility with different Turbo versions
    const success = event.detail?.success ?? event.detail?.fetchResponse?.succeeded
    if (!success) return

    // Get provider from the form's hidden field
    const providerInput = this.element.querySelector('input[name="provider"]')
    const provider = providerInput?.value || this.providerValue

    if (!provider) return

    // Dispatch at document level since drawer is rendered outside panel DOM
    document.dispatchEvent(new CustomEvent("attribution:settings-saved", {
      detail: { provider }
    }))

    this.closeDrawer()
  }

  /**
   * Closes the drawer by finding and clicking the cancel button (which has force param).
   * Uses the drawer identifier from the drawer--touched controller on this form.
   */
  closeDrawer() {
    const drawerIdentifier = this.element.getAttribute("data-drawer--touched-identifier-value")
    if (!drawerIdentifier) return

    // The drawer element ID is the identifier + "-drawer" suffix (from DrawerComponent)
    const drawerElementId = `${drawerIdentifier}-drawer`
    const drawerElement = document.getElementById(drawerElementId)
    if (!drawerElement) return

    // Find the cancel button which has the force close param, or fall back to close button
    const cancelButton = drawerElement.querySelector('[data-drawer-instance-close-param="force"]')
    if (cancelButton) {
      cancelButton.click()
      return
    }

    const overlay = drawerElement.querySelector('[data-drawer-instance-target="overlay"]')
    if (overlay) {
      overlay.click()
    }
  }
}
